import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Group,
  Loader,
  Select,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconInfoCircle, IconWand } from '@tabler/icons-react';
import { PageContainer } from '../../components/ui/PageContainer';
import {
  useAssignmentMatrix,
  usePrefillAssignments,
  useSetAssignment,
} from '../../hooks/useQuarterlyScoreApi';
import { reportingQuarter } from '../../components/essays/quarterPeriod';
import { useTeams } from '../../hooks/useTeamApi';
import { showToast } from '../../utils/toast';

/**
 * Матрица назначений: кто кого оценивает за квартал.
 *
 * Строки — сотрудники, столбцы — оценивающие. В столбцы попадают только те, у
 * кого есть хоть одно назначение: на 28 сотрудниках полная матрица дала бы
 * восемьсот ячеек, из которых значимы единицы.
 */
export const QuarterlyScoringMatrix: React.FC = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(() => reportingQuarter());
  const { data, isLoading } = useAssignmentMatrix(period.year, period.quarter);
  const { data: teams } = useTeams();
  const [teamId, setTeamId] = useState<string | null>(null);
  const setAssignment = useSetAssignment();
  const prefill = usePrefillAssignments();

  const quarterOptions = useMemo(
    () =>
      [1, 2, 3, 4].map((q) => ({ value: String(q), label: t('quarterly.quarterOption', { q }) })),
    [t],
  );
  const yearOptions = useMemo(() => {
    const y = dayjs().year();
    return Array.from({ length: 5 }, (_, i) => String(y - 3 + i));
  }, []);

  const assignedKeys = useMemo(
    () => new Set((data?.assignments || []).map((a) => `${a.author}:${a.subject}`)),
    [data],
  );

  // Столбцы — те, кто кого-то оценивает, плюс добавленные вручную. Показывать
  // весь штат нельзя: на 28 сотрудниках это 800 ячеек с единичными галочками.
  // Но и ограничиться назначенными тоже нельзя — тогда нового оценивающего
  // некуда поставить, а ручная правка матрицы и есть весь смысл.
  const [extraAuthorIds, setExtraAuthorIds] = useState<string[]>([]);

  const authors = useMemo(() => {
    const ids = new Set([...(data?.assignments || []).map((a) => a.author), ...extraAuthorIds]);
    return (data?.people || []).filter((p) => ids.has(p.id));
  }, [data, extraAuthorIds]);

  const addableAuthors = useMemo(() => {
    const shown = new Set(authors.map((a) => a.id));
    return (data?.people || [])
      .filter((p) => !shown.has(p.id))
      .map((p) => ({ value: p.id, label: p.name }));
  }, [data, authors]);

  const teamOptions = useMemo(
    () => (teams || []).map((team) => ({ value: team._id, label: team.name })),
    [teams],
  );

  /**
   * Фильтр по команде сужает только СТРОКИ. Столбцы остаются: главный
   * тестировщик оценивает людей из всех команд, и, отфильтровав его вместе со
   * строками, мы бы спрятали половину назначений.
   */
  const teamMemberIds = useMemo(() => {
    const team = (teams || []).find((x) => x._id === teamId);
    if (!team) return null;
    const ids = new Set<string>(
      (team.members || []).map((m: { user?: { _id?: string } | string }) =>
        typeof m.user === 'object' ? String(m.user?._id) : String(m.user),
      ),
    );
    if (team.lead?._id) ids.add(team.lead._id);
    if (team.pm?._id) ids.add(team.pm._id);
    return ids;
  }, [teams, teamId]);

  const rows = useMemo(
    () => (data?.people || []).filter((p) => !teamMemberIds || teamMemberIds.has(p.id)),
    [data, teamMemberIds],
  );

  const loadOf = (authorId: string) =>
    (data?.assignments || []).filter((a) => a.author === authorId).length;

  const toggle = (subject: string, author: string) =>
    setAssignment.mutate({
      year: period.year,
      quarter: period.quarter,
      subject,
      author,
      isAssigned: !assignedKeys.has(`${author}:${subject}`),
    });

  const runPrefill = () =>
    modals.openConfirmModal({
      title: t('quarterlyScoring.matrix.prefill'),
      centered: true,
      children: <Text size="sm">{t('quarterlyScoring.matrix.prefillHint')}</Text>,
      labels: { confirm: t('quarterlyScoring.matrix.prefill'), cancel: t('common.cancel') },
      onConfirm: () =>
        prefill.mutate(period, {
          onSuccess: ({ added }) =>
            showToast({
              title: t('quarterlyScoring.matrix.prefill'),
              message: t('quarterlyScoring.matrix.prefillDone', { count: added }),
              color: 'teal',
            }),
        }),
    });

  return (
    <PageContainer>
      <Group justify="space-between" mb="lg" wrap="wrap">
        <Title order={2}>{t('quarterlyScoring.matrix.title')}</Title>
        <Group gap="sm">
          <Select
            w={150}
            data={quarterOptions}
            value={String(period.quarter)}
            onChange={(v) => v && setPeriod((p) => ({ ...p, quarter: Number(v) }))}
            allowDeselect={false}
          />
          <Select
            w={110}
            data={yearOptions}
            value={String(period.year)}
            onChange={(v) => v && setPeriod((p) => ({ ...p, year: Number(v) }))}
            allowDeselect={false}
          />
          <Select
            w={200}
            clearable
            data={teamOptions}
            value={teamId}
            onChange={setTeamId}
            placeholder={t('quarterlyScoring.matrix.allTeams')}
          />
          <Select
            w={220}
            searchable
            clearable
            data={addableAuthors}
            value={null}
            onChange={(id) => id && setExtraAuthorIds((prev) => [...prev, id])}
            placeholder={t('quarterlyScoring.matrix.addAuthor')}
            nothingFoundMessage={t('quarterlyScoring.matrix.addAuthorEmpty')}
          />
          <Button
            leftSection={<IconWand size={16} />}
            variant="light"
            onClick={runPrefill}
            loading={prefill.isPending}
          >
            {t('quarterlyScoring.matrix.prefill')}
          </Button>
        </Group>
      </Group>

      {isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : authors.length === 0 ? (
        <Alert variant="light" color="gray" icon={<IconInfoCircle size={16} />}>
          {t('quarterlyScoring.matrix.empty')}
        </Alert>
      ) : (
        <Card withBorder radius="md" padding={0}>
          {/* Матрица шире экрана уже на десятке оценивающих — скроллим её саму,
              а не страницу. */}
          <Box style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ minWidth: 220 }}>
                    {t('quarterlyScoring.matrix.person')}
                  </Table.Th>
                  {authors.map((author) => (
                    <Table.Th key={author.id} style={{ minWidth: 120 }}>
                      <Text size="xs" fw={600}>
                        {author.name}
                      </Text>
                      {/* Нагрузка видна до открытия окна: предзаполнение даёт
                          главному тестировщику весь штат. */}
                      <Badge size="xs" variant="light" mt={4}>
                        {t('quarterlyScoring.matrix.load', { count: loadOf(author.id) })}
                      </Badge>
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((person) => (
                  <Table.Tr key={person.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {person.name}
                      </Text>
                      {person.position && (
                        <Text size="xs" c="dimmed">
                          {person.position}
                        </Text>
                      )}
                    </Table.Td>
                    {authors.map((author) => (
                      <Table.Td key={author.id} style={{ textAlign: 'center' }}>
                        {author.id === person.id ? (
                          <Text size="xs" c="dimmed">
                            —
                          </Text>
                        ) : (
                          <Checkbox
                            checked={assignedKeys.has(`${author.id}:${person.id}`)}
                            onChange={() => toggle(person.id, author.id)}
                            aria-label={`${author.name} → ${person.name}`}
                          />
                        )}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        </Card>
      )}
    </PageContainer>
  );
};
